<template>
  <div
    class="searchable-select"
    :class="{ 'is-open': open, 'is-disabled': disabled }"
  >
    <label v-if="label" :for="inputId" class="searchable-select-label">{{ label }}</label>
    <div class="searchable-select-control">
      <input
        :id="inputId"
        ref="input"
        type="text"
        class="searchable-select-input"
        role="combobox"
        :aria-expanded="open ? 'true' : 'false'"
        :aria-controls="listId"
        :aria-activedescendant="activeDescendant"
        aria-autocomplete="list"
        :aria-label="ariaLabel || label || placeholder"
        :placeholder="displayPlaceholder"
        :disabled="disabled"
        :value="inputText"
        autocomplete="off"
        @focus="onFocus"
        @input="onInput"
        @keydown="onKeydown"
        @blur="onBlur"
      />
      <button
        v-if="modelValue"
        type="button"
        class="searchable-select-clear"
        :disabled="disabled"
        aria-label="Clear selection"
        tabindex="-1"
        @mousedown.prevent
        @click="clearSelection"
      >
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="searchable-select-toggle"
        :disabled="disabled"
        tabindex="-1"
        :aria-label="open ? 'Close options' : 'Open options'"
        @mousedown.prevent
        @click="toggleOpen"
      >
        <i :class="open ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" aria-hidden="true"></i>
      </button>
    </div>
    <ul
      v-show="open"
      :id="listId"
      class="searchable-select-list"
      role="listbox"
      :aria-label="ariaLabel || label || 'Options'"
    >
      <li
        v-if="allowEmpty"
        :id="optionId('__all__')"
        role="option"
        class="searchable-select-option"
        :class="{ active: highlightIndex === 0, selected: !modelValue }"
        :aria-selected="!modelValue ? 'true' : 'false'"
        @mousedown.prevent
        @click="selectOption(null)"
      >
        {{ emptyLabel }}
      </li>
      <li
        v-for="(opt, idx) in filteredOptions"
        :id="optionId(opt.value)"
        :key="opt.value"
        role="option"
        class="searchable-select-option"
        :class="{
          active: highlightIndex === idx + emptyOffset,
          selected: modelValue === opt.value
        }"
        :aria-selected="modelValue === opt.value ? 'true' : 'false'"
        @mousedown.prevent
        @click="selectOption(opt)"
      >
        {{ opt.label }}
      </li>
      <li
        v-if="filteredOptions.length === 0"
        class="searchable-select-empty"
        role="presentation"
      >
        No matching ports
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * CP-80: Lightweight type-to-filter select (combobox) for long option lists.
 * Options: [{ value, label }]. v-model is the selected value (canonical key).
 */
let uid = 0;

export default {
  name: 'SearchableSelect',
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: ''
    },
    ariaLabel: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Search…'
    },
    emptyLabel: {
      type: String,
      default: 'All'
    },
    allowEmpty: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    uid += 1;
    return {
      open: false,
      query: '',
      typing: false,
      highlightIndex: 0,
      instanceId: `ss-${uid}`
    };
  },
  computed: {
    inputId() {
      return this.id || `${this.instanceId}-input`;
    },
    listId() {
      return `${this.instanceId}-list`;
    },
    emptyOffset() {
      return this.allowEmpty ? 1 : 0;
    },
    selectedOption() {
      if (!this.modelValue) return null;
      return this.options.find((o) => o && o.value === this.modelValue) || null;
    },
    inputText() {
      if (this.typing || this.open) return this.query;
      return this.selectedOption ? this.selectedOption.label : '';
    },
    displayPlaceholder() {
      if (this.selectedOption && !this.open) return this.selectedOption.label;
      return this.placeholder;
    },
    filteredOptions() {
      const q = String(this.query || '').trim().toLowerCase();
      if (!q) return this.options;
      return this.options.filter((o) => {
        const label = String((o && o.label) || '').toLowerCase();
        const value = String((o && o.value) || '').toLowerCase();
        return label.includes(q) || value.includes(q);
      });
    },
    activeDescendant() {
      if (!this.open) return undefined;
      if (this.highlightIndex === 0 && this.allowEmpty) return this.optionId('__all__');
      const opt = this.filteredOptions[this.highlightIndex - this.emptyOffset];
      return opt ? this.optionId(opt.value) : undefined;
    }
  },
  watch: {
    modelValue() {
      if (!this.open) {
        this.query = '';
        this.typing = false;
      }
    },
    open(isOpen) {
      if (isOpen) {
        this.highlightIndex = this.indexOfValue(this.modelValue);
        this.$nextTick(() => this.scrollHighlighted());
      } else {
        this.query = '';
        this.typing = false;
      }
    }
  },
  methods: {
    optionId(value) {
      return `${this.instanceId}-opt-${String(value).replace(/\s+/g, '_')}`;
    },
    indexOfValue(value) {
      if (!value) return 0;
      const idx = this.filteredOptions.findIndex((o) => o.value === value);
      return idx >= 0 ? idx + this.emptyOffset : 0;
    },
    onFocus() {
      if (this.disabled) return;
      this.open = true;
      this.typing = true;
      this.query = '';
    },
    onInput(event) {
      this.typing = true;
      this.query = event.target.value;
      this.open = true;
      this.highlightIndex = 0;
    },
    onBlur() {
      // Delay so option mousedown/click can run first.
      window.setTimeout(() => {
        this.open = false;
        this.typing = false;
        this.query = '';
      }, 120);
    },
    toggleOpen() {
      if (this.disabled) return;
      this.open = !this.open;
      if (this.open) {
        this.typing = true;
        this.query = '';
        this.$nextTick(() => {
          if (this.$refs.input) this.$refs.input.focus();
        });
      }
    },
    clearSelection() {
      this.emitValue('');
      this.query = '';
      this.typing = false;
      this.open = false;
    },
    selectOption(opt) {
      if (!opt) {
        this.emitValue('');
      } else {
        this.emitValue(opt.value);
      }
      this.open = false;
      this.typing = false;
      this.query = '';
    },
    emitValue(value) {
      const next = value == null ? '' : String(value);
      this.$emit('update:modelValue', next);
      this.$emit('change', next);
    },
    onKeydown(event) {
      const max = this.filteredOptions.length - 1 + this.emptyOffset;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!this.open) this.open = true;
        this.highlightIndex = Math.min(max, this.highlightIndex + 1);
        this.scrollHighlighted();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!this.open) this.open = true;
        this.highlightIndex = Math.max(0, this.highlightIndex - 1);
        this.scrollHighlighted();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (!this.open) {
          this.open = true;
          return;
        }
        if (this.allowEmpty && this.highlightIndex === 0) {
          this.selectOption(null);
          return;
        }
        const opt = this.filteredOptions[this.highlightIndex - this.emptyOffset];
        if (opt) this.selectOption(opt);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.open = false;
        this.query = '';
        this.typing = false;
      }
    },
    scrollHighlighted() {
      const id = this.activeDescendant;
      if (!id) return;
      const el = document.getElementById(id);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest' });
      }
    }
  }
};
</script>

<style scoped>
.searchable-select {
  position: relative;
  width: 100%;
  min-width: 0;
}

.searchable-select-label {
  display: block;
  margin-bottom: var(--space-2);
  font-weight: 600;
  color: var(--color-text-muted);
  font-size: var(--text-small);
}

.searchable-select-control {
  position: relative;
  display: flex;
  align-items: stretch;
}

.searchable-select-input {
  width: 100%;
  min-height: var(--touch-min, 44px);
  padding: var(--space-3);
  padding-right: 4.5rem;
  border: 1px solid var(--color-border, var(--cs-card-border));
  border-radius: var(--radius-sm, 8px);
  font-size: var(--text-body, 1rem);
  background: var(--color-surface, var(--cs-card));
  color: var(--color-text, var(--cs-ink));
  box-sizing: border-box;
}

.searchable-select-input:focus {
  outline: none;
  border-color: var(--color-primary, var(--cs-signal-teal));
  box-shadow: 0 0 0 2px var(--color-primary-light, var(--cs-signal-mist));
}

.searchable-select-clear,
.searchable-select-toggle {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  color: var(--color-text-muted, var(--cs-muted));
  cursor: pointer;
}

.searchable-select-clear {
  right: 2.25rem;
}

.searchable-select-toggle {
  right: 0.35rem;
}

.searchable-select-list {
  position: absolute;
  z-index: 30;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  max-height: 240px;
  overflow-y: auto;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: var(--color-surface, var(--cs-card));
  border: 1px solid var(--color-border, var(--cs-card-border));
  border-radius: var(--radius-sm, 8px);
  box-shadow: var(--shadow-card, 0 8px 24px rgba(0, 0, 0, 0.12));
}

.searchable-select-option {
  padding: 10px 12px;
  cursor: pointer;
  font-size: var(--text-small, 0.925rem);
  color: var(--color-text, var(--cs-ink));
}

.searchable-select-option:hover,
.searchable-select-option.active {
  background: var(--cs-signal-mist, #e7f3f4);
  color: var(--cs-signal-teal, #0b5b63);
}

.searchable-select-option.selected {
  font-weight: 600;
}

.searchable-select-empty {
  padding: 10px 12px;
  color: var(--color-text-muted, var(--cs-muted));
  font-size: var(--text-small, 0.925rem);
}

.searchable-select.is-disabled {
  opacity: 0.65;
  pointer-events: none;
}
</style>
